import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { api } from "../api";
import { t } from "../i18n/sl";
import { useSession } from "../lib/session";
import { Icon, type IconName } from "../components/Icon";
import { ProductMark } from "./ProductMark";
import { LegalLine } from "./Footer";
import { JobsProvider } from "../lib/jobs";
import { ImportJobProvider } from "../lib/importJob";

const NAV: { to: string; label: string; icon: IconName; end?: boolean }[] = [
  { to: "/app", label: t.nav.overview, icon: "overview", end: true },
  { to: "/app/pogovor", label: t.nav.chat, icon: "chat" },
  { to: "/app/register", label: t.nav.register, icon: "register" },
  { to: "/app/anketa", label: t.nav.survey, icon: "survey" },
  { to: "/app/pismenost", label: t.nav.literacy, icon: "book" },
  { to: "/app/dokumenti", label: t.nav.documents, icon: "file" },
];

export function AppShell() {
  const { me, refresh } = useSession();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => setOpen(false), [loc.pathname]);

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      await refresh();
      navigate("/");
    }
  };

  const wide = loc.pathname.startsWith("/app/pogovor");

  return (
    <div className={`shell ${open ? "is-nav-open" : ""}`}>
      <a href="#main" className="skip-link">
        {t.nav.skip}
      </a>
      <header className="shell-topbar">
        <ProductMark to="/app" sub={false} />
        <button className="icon-btn" aria-expanded={open} aria-controls="shell-nav" onClick={() => setOpen((o) => !o)}>
          <Icon name={open ? "x" : "menu"} size={22} />
          <span className="sr-only">{t.nav.menu}</span>
        </button>
      </header>
      <aside id="shell-nav" className="shell-nav">
        <div className="shell-nav-top">
          <ProductMark to="/app" />
        </div>
        {me?.org && (
          <NavLink to="/app/profil" className="shell-org">
            <Icon name="building" size={16} />
            <span className="shell-org-name">{me.org.name}</span>
          </NavLink>
        )}
        <nav aria-label={t.nav.appNav} className="shell-links">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className="shell-link">
              <Icon name={n.icon} size={19} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="shell-nav-bottom">
          {me?.is_admin && (
            <NavLink to="/admin" className="shell-link shell-link--quiet">
              <Icon name="shield" size={18} />
              {t.nav.admin}
            </NavLink>
          )}
          <div className="shell-user">
            <span className="shell-user-email" title={me?.user?.email}>
              {me?.user?.email}
            </span>
            <button className="icon-btn" onClick={logout} title={t.nav.logout}>
              <Icon name="logout" size={18} />
              <span className="sr-only">{t.nav.logout}</span>
            </button>
          </div>
        </div>
      </aside>
      <div className="shell-scrim" onClick={() => setOpen(false)} aria-hidden="true" />
      <main id="main" className={`shell-main ${wide ? "shell-main--wide" : ""}`}>
        <div className="shell-content">
          <JobsProvider>
            <ImportJobProvider>
              <Outlet />
            </ImportJobProvider>
          </JobsProvider>
        </div>
        {!wide && <LegalLine className="shell-legal" privacy="same" />}
      </main>
    </div>
  );
}
